// Gait-event detection — the heart of Phase 1.
//
// From a stream of PoseFrames we derive, per foot:
//   • Heel-strike (initial contact)  — foot most ANTERIOR relative to pelvis
//   • Toe-off (terminal contact)     — foot most POSTERIOR relative to pelvis
// and segment the stream into gait cycles (HS → next ipsilateral HS) with
// stance/swing sub-phases.
//
// Method (lateral view, the primary view for cycle detection):
//   s_side(t) = footIndex.x - pelvisCenter.x   (signed AP proxy, image space)
// We orient s so that "forward" is positive using the dominant travel
// direction, then peaks(s) = heel strikes, valleys(s) = toe-offs.
//
// Everything here is pure TS over PoseFrame[] so it unit-tests against
// synthetic fixtures with no browser/MediaPipe dependency.

import { Joints, type PoseFrame, type Side } from "../pose/landmarks";
import { oneEuroSeries, coefficientOfVariation } from "../pose/smoothing";
import { findPeaks, negate, mean } from "./signal";

export type EventType = "HS" | "TO";

export interface GaitEvent {
  side: Side;
  type: EventType;
  tMs: number;
  index: number;
  confidence: number;
}

export interface GaitCycle {
  side: Side;
  startMs: number; // heel strike
  endMs: number; // next ipsilateral heel strike
  durationMs: number;
  toeOffMs: number | null;
  stanceMs: number | null;
  swingMs: number | null;
  stancePct: number | null;
}

export interface EventResult {
  events: GaitEvent[];
  cycles: GaitCycle[];
  meanVisibility: number;
  /** Travel-direction sign applied to the AP proxy (+1 or -1). */
  direction: number;
}

export type GaitView = "frontal" | "lateral";

export interface DetectOptions {
  /** Capture view — selects the event-detection signal. Default "lateral". */
  view?: GaitView;
  /** Minimum seconds between same-type events (rejects double-detections). */
  minEventGapSec?: number;
  /** Minimum prominence as a fraction of the signal's own amplitude. */
  minProminenceFrac?: number;
}

export function detectGaitEvents(
  frames: PoseFrame[],
  opts: DetectOptions = {},
): EventResult {
  const view = opts.view ?? "lateral";
  const minGap = opts.minEventGapSec ?? 0.25;
  const promFrac = opts.minProminenceFrac ?? 0.15;

  const ts = frames.map((f) => f.tMs);
  const dtMed = medianDt(ts);
  const minDist = Math.max(1, Math.round((minGap * 1000) / dtMed));

  // Determine travel direction from pelvis-center horizontal drift.
  const pelvisX = frames.map((f) => new Joints(f).pelvisCenter().x);
  const direction = Math.sign(pelvisX[pelvisX.length - 1] - pelvisX[0]) || 1;

  const events: GaitEvent[] = [];
  for (const side of ["L", "R"] as Side[]) {
    if (view === "lateral") {
      // Sagittal: AP foot position relative to pelvis. Forward extremum = heel
      // strike; backward extremum = toe-off.
      const raw = frames.map((f) => {
        const j = new Joints(f);
        return (j.footIndex(side).x - j.pelvisCenter().x) * direction;
      });
      const s = oneEuroSeries(raw, ts, { minCutoff: 1.5, beta: 0.01 });
      const amp = Math.max(...s) - Math.min(...s) || 1;
      const minProm = promFrac * amp;
      for (const p of findPeaks(s, minDist, minProm)) {
        events.push(mkEvent(side, "HS", ts[p.index], p.index, frames[p.index]));
      }
      for (const p of findPeaks(negate(s), minDist, minProm)) {
        events.push(mkEvent(side, "TO", ts[p.index], p.index, frames[p.index]));
      }
    } else {
      // Frontal: AP motion is in depth, not image-x. Use the ankle's VERTICAL
      // position — the foot reaches its lowest point (max image-y) when planted.
      // We detect initial contacts here; reliable toe-off is not available from
      // the frontal view, so stance/swing split is deferred to a lateral clip.
      const raw = frames.map((f) => new Joints(f).ankle(side).y);
      const s = oneEuroSeries(raw, ts, { minCutoff: 1.5, beta: 0.01 });
      const amp = Math.max(...s) - Math.min(...s) || 1;
      const minProm = promFrac * amp;
      for (const p of findPeaks(s, minDist, minProm)) {
        events.push(mkEvent(side, "HS", ts[p.index], p.index, frames[p.index]));
      }
    }
  }

  events.sort((a, b) => a.tMs - b.tMs);
  const cycles = buildCycles(events);
  return {
    events,
    cycles,
    meanVisibility: meanFrameVisibility(frames),
    direction,
  };
}

function mkEvent(
  side: Side,
  type: EventType,
  tMs: number,
  index: number,
  frame: PoseFrame,
): GaitEvent {
  return { side, type, tMs, index, confidence: visibilityAt(frame, side) };
}

/**
 * Single-limb stance windows for a side: from this side's contact until the
 * other side's next contact. Used by frontal feature extraction to evaluate
 * pelvic drop / knee collapse during the relevant stance phase.
 */
export function singleSupportWindows(
  events: GaitEvent[],
  side: Side,
): Array<{ startMs: number; endMs: number }> {
  const hs = events.filter((e) => e.type === "HS").sort((a, b) => a.tMs - b.tMs);
  const windows: Array<{ startMs: number; endMs: number }> = [];
  for (let i = 0; i < hs.length; i++) {
    if (hs[i].side !== side) continue;
    const next = hs.find((e) => e.tMs > hs[i].tMs && e.side !== side);
    if (next) windows.push({ startMs: hs[i].tMs, endMs: next.tMs });
  }
  return windows;
}

/** Pair consecutive ipsilateral heel strikes into cycles; locate the toe-off between. */
function buildCycles(events: GaitEvent[]): GaitCycle[] {
  const cycles: GaitCycle[] = [];
  for (const side of ["L", "R"] as Side[]) {
    const hs = events.filter((e) => e.side === side && e.type === "HS");
    const to = events.filter((e) => e.side === side && e.type === "TO");
    for (let i = 0; i < hs.length - 1; i++) {
      const startMs = hs[i].tMs;
      const endMs = hs[i + 1].tMs;
      const durationMs = endMs - startMs;
      const toe = to.find((e) => e.tMs > startMs && e.tMs < endMs);
      const toeOffMs = toe ? toe.tMs : null;
      const stanceMs = toeOffMs !== null ? toeOffMs - startMs : null;
      const swingMs = toeOffMs !== null ? endMs - toeOffMs : null;
      const stancePct =
        stanceMs !== null && durationMs > 0 ? (stanceMs / durationMs) * 100 : null;
      cycles.push({
        side,
        startMs,
        endMs,
        durationMs,
        toeOffMs,
        stanceMs,
        swingMs,
        stancePct,
      });
    }
  }
  cycles.sort((a, b) => a.startMs - b.startMs);
  return cycles;
}

function medianDt(ts: number[]): number {
  if (ts.length < 2) return 16.7;
  const diffs: number[] = [];
  for (let i = 1; i < ts.length; i++) diffs.push(ts[i] - ts[i - 1]);
  diffs.sort((a, b) => a - b);
  return diffs[Math.floor(diffs.length / 2)] || 16.7;
}

function visibilityAt(f: PoseFrame, side: Side): number {
  const j = new Joints(f);
  return Math.min(j.heel(side).visibility, j.footIndex(side).visibility);
}

function meanFrameVisibility(frames: PoseFrame[]): number {
  return mean(
    frames.map((f) => mean(f.landmarks.map((l) => l.visibility))),
  );
}

/** Convenience: cycle-duration coefficient of variation (consistency proxy). */
export function cycleDurationCV(cycles: GaitCycle[]): number {
  return coefficientOfVariation(cycles.map((c) => c.durationMs));
}
